import React, { useEffect, useState } from "react"

import { ReactComponent as TeacherIcon } from "@brand_assets/svg/teacher_icon.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, Typography } from "@mui/material"
import { useMutationAppleLogin } from "api/reactQuery/mutations/appleLogin"
import { useMutationGoogleLogin } from "api/reactQuery/mutations/googleLogin"
import { useMutationPostTeacher } from "api/reactQuery/mutations/teacher"
import { useQueryTeacherCleverLogin } from "api/reactQuery/queries/teacherCleverLogin"
import { useQueryTeacherRegistrationData } from "api/reactQuery/queries/teacherRegistrationData"
import { IResponseTeacherRegistrationData } from "api/reactQuery/queries/teacherRegistrationData.types"
import { AxiosError } from "axios"
import AppleButton from "components/common/appleButton"
import CleverButton from "components/common/button/cleverButton"
import RegisterButtons from "components/common/button/registerButtons"
import CleverRestrictedTakeover from "components/common/cleverRestrictedTakeover"
import CleverSSOLoader from "components/common/cleverSSOLoader"
import GoogleButton from "components/common/googleButton"
import OpenIdSSOLoaders from "components/common/openIdSSOLoaders"
import RegisterInformation from "components/common/registerInformation"
import RegisterInput from "components/common/textField/registerInput"
import { useApple } from "hooks/apple"
import { useCleverRestrictedError } from "hooks/cleverRestrictedError/useCleverRestrictedError"
import { useCleverSSO } from "hooks/cleverSSO/useCleverSSO"
import { useGoogle } from "hooks/google"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppDispatch, useAppSelector } from "hooks/store"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { selectRegistrationData } from "store/auth/auth.selectors"
import {
  setActivePageSignUp,
  setAuthData,
  setTeacherRegistrationData,
} from "store/auth/auth.slice"
import { UserRole } from "ts/enums/User"
import { getErrorMessage, getErrorMessageFromTab } from "utils/api"

import CheckboxField from "./checkboxField"
import { defaultValues, validationSchema } from "./SignAsTeacher.config"
import { ITeacherState } from "./SignAsTeacher.types"
import useSaveUTMs from "./useSaveUTMs"

const SignAsTeacher = () => {
  const dispatch = useAppDispatch()
  const { showSnackbar } = useCustomSnackbar()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const teacherRegisterData = useAppSelector(selectRegistrationData)

  const [checked, setChecked] = useState(false)
  const [error, setError] = useState("")
  const { isLocked, handleCleverRestrictedError, resetLock } =
    useCleverRestrictedError()
  const { isCleverSSOLoading, handleCleverReset, handleCleverAuthSuccess } =
    useCleverSSO({ userType: "teacher", isSignup: true })
  const [searchParams, setSearchParams] = useSearchParams()

  const methods = useForm<ITeacherState>({
    resolver: yupResolver(validationSchema),
    defaultValues: defaultValues,
  })

  useSaveUTMs()

  useEffect(() => {
    if (localStorage.getItem("token")) {
      setSearchParams({ token: localStorage.getItem("token") ?? "" })
    }
  }, [])

  useEffect(() => {
    const isStripeEnabled = searchParams.has("isStripe")
    if (isStripeEnabled) {
      sessionStorage.setItem("specialLink", JSON.stringify(isStripeEnabled))
    }
  }, [searchParams])

  useQueryTeacherRegistrationData({
    token: window.location.search.slice(7),
    options: {
      enabled: !!searchParams.get("token"),
      onSuccess: (data) => {
        dispatch(
          setTeacherRegistrationData(
            data?.data as IResponseTeacherRegistrationData,
          ),
        )
      },
    },
  })

  useEffect(() => {
    methods.setValue(
      "email",
      !!teacherRegisterData ? teacherRegisterData.email : "",
    )
  }, [teacherRegisterData])

  useEffect(() => {
    if (checked) {
      setError("")
    }
  }, [checked])

  const { mutate: createTeacher, isLoading: isLoadingCreateTeacher } =
    useMutationPostTeacher({
      options: {
        onSuccess: (data) => {
          showSnackbar({
            title: t("noAuthFlow.emailAndPasswordCreated"),
          })
          localStorage.removeItem("token")

          dispatch(
            setAuthData({
              token: data.data.access_token,
              finalized: !data.data.finalization_required,
              role: UserRole.TEACHER,
              is_email_confirmed: data.data.is_email_confirmed,
            }),
          )
          {
            data.data.finalization_required
              ? dispatch(setActivePageSignUp(2))
              : navigate(RoutePath.HOMEPAGE)
          }
        },
        onError: (err) => {
          if (handleCleverRestrictedError(err)) return

          showSnackbar({
            title: t(getErrorMessageFromTab(err)),
            variant: "error",
            options: { autoHideDuration: 15000 },
          })
        },
      },
    })

  const onSubmit = async (data: { email: string; password: string }) => {
    createTeacher({
      email: data.email,
      password: data.password,
    })
  }

  const handleSSOSuccess = (data: {
    data: { access_token: string; finalization_required: boolean }
  }) => {
    localStorage.removeItem("token")
    const { access_token, finalization_required } = data.data
    dispatch(
      setAuthData({
        token: access_token,
        role: UserRole.TEACHER,
        finalized: !finalization_required,
        is_email_confirmed: true,
      }),
    )
    showSnackbar({
      title: t(
        finalization_required
          ? "noAuthFlow.emailAndPasswordCreated"
          : "noAuthFlow.loginSuccessful",
      ),
    })
    if (finalization_required) {
      dispatch(setActivePageSignUp(2))
    } else {
      navigate(RoutePath.HOMEPAGE)
    }
  }

  const handleSSOError = (err: AxiosError) => {
    if (handleCleverRestrictedError(err)) return
    showSnackbar({ title: t(getErrorMessage(err)), variant: "error" })
  }

  const { mutate } = useMutationGoogleLogin({
    option: { onSuccess: handleSSOSuccess, onError: handleSSOError },
  })

  const { refButton } = useGoogle({ mutate })

  const { mutate: appleMutate } = useMutationAppleLogin({
    option: { onSuccess: handleSSOSuccess, onError: handleSSOError },
  })

  const { signIn: appleSignIn } = useApple({ mutate: appleMutate })

  const withTermsCheck = (action: () => void) => () => {
    if (!checked) {
      setError(t("validations.checkTerms"))
      return
    }
    action()
  }

  const { refetch: cleverSignup } = useQueryTeacherCleverLogin()

  const handleResetLock = () => {
    resetLock()
    methods.reset()
    setChecked(false)
    setError("")
  }

  return (
    <>
      <CleverSSOLoader
        isLoading={isCleverSSOLoading}
        userType="teacher"
        onReset={handleCleverReset}
        onAuthSuccess={handleCleverAuthSuccess}
      />
      <OpenIdSSOLoaders userType="teacher" isSignup />
      {isLocked ? (
        <CleverRestrictedTakeover
          icon={TeacherIcon}
          onSignInWithClever={() => cleverSignup()}
          onReset={handleResetLock}
        />
      ) : (
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <FormProvider {...methods}>
            <Box textAlign="left">
              <Typography
                variant="h2"
                component="p"
                color="primary.main"
                fontWeight="600"
              >
                {t("noAuthFlow.teacher")}
              </Typography>
              <Typography
                component="h1"
                tabIndex={-1}
                variant="body1"
                fontSize="24px"
                fontWeight="500"
                color="mockup.neutral50"
                sx={{ pt: 1, pb: 4 }}
              >
                {t("noAuthFlow.signup")}
              </Typography>
            </Box>
            <GoogleButton
              text={t("noAuthFlow.signUpGoogle")}
              onClick={() => !checked && setError(t("validations.checkTerms"))}
              checked={checked}
              isSignUp
              ref={refButton}
            />
            <Box sx={{ mt: 2 }}>
              <AppleButton
                text={t("noAuthFlow.signUpApple")}
                onClick={withTermsCheck(appleSignIn)}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <CleverButton
                text="SIGN UP WITH CLEVER"
                onClick={withTermsCheck(cleverSignup)}
              />
            </Box>
            <Typography variant="subtitle1" color="mockup.grey80" py={2}>
              {t("defaultTranslations.or")}
            </Typography>
            <Box>
              <RegisterInput name="email" type="email" isSignUp />
              <CheckboxField
                name="terms"
                error={error}
                onClick={() => setChecked(!checked)}
              />
              <RegisterButtons
                isLoading={isLoadingCreateTeacher}
                submitButton={t("noAuthFlow.signup")}
                onClick={() =>
                  (window.location.href = "https://www.classbank.com/")
                }
              />
              <RegisterInformation isSignInOrUp />
            </Box>
          </FormProvider>
        </form>
      )}
    </>
  )
}

export default SignAsTeacher
